import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../../lib/motion";
import "./Icon3D.css";

const Icon3DCanvas = lazy(() => import("./Icon3DCanvas"));

/** WebGL support is probed once and cached for the whole session. */
let webglOk = null;
function hasWebGL() {
  if (webglOk !== null) return webglOk;
  try {
    const c = document.createElement("canvas");
    webglOk = !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    webglOk = false;
  }
  return webglOk;
}

/**
 * Decides whether this device gets to download three.js at all.
 *
 * The cores/memory heuristic alone was far too permissive: modern phones report
 * 8 cores and 8 GB, so handsets sailed through the gate and paid ~900ms of
 * scripting for a 235 KB chunk they can least afford. The pointer query is the
 * signal that actually separates phones from desktops — a phone browser always
 * reports a coarse pointer with no hover, no matter how fast its CPU is.
 *
 * Everything here is a veto, and the result is cached for the session so a page
 * full of icons probes the environment once.
 */
let deviceOk = null;
function canAfford3D() {
  if (deviceOk !== null) return deviceOk;

  // Primary phone/tablet filter.
  const desktopPointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  // Second opinion for stylus/desktop-mode browsers that fake a fine pointer.
  const wideEnough = window.innerWidth >= 1024;
  // Chromium-only, so it is `undefined` elsewhere and can only ever veto.
  const mobileUA = navigator.userAgentData?.mobile === true;
  // Data Saver is an explicit "don't spend my bytes".
  const saveData = navigator.connection?.saveData === true;
  // Kept from the original gate as a floor for old laptops.
  const cores = navigator.hardwareConcurrency ?? 8;
  const mem = navigator.deviceMemory ?? 8;

  deviceOk =
    desktopPointer && wideEnough && !mobileUA && !saveData && cores > 3 && mem > 2;
  return deviceOk;
}

/**
 * Renders the reference's original illustration, then upgrades it in place to a
 * lit, shadow-casting 3D model once the icon scrolls into view.
 *
 * The three.js bundle is a lazy chunk, the canvas only mounts while visible,
 * and everything degrades to the flat asset when the device class, WebGL or the
 * visitor's motion preference says no. Because `<Icon3DCanvas>` is the only
 * reference to the lazy component, not rendering it means React never calls the
 * `import()` factory and the chunk is never requested over the network.
 */
/**
 * Size is intentionally NOT a prop: it comes from the `--icon3d-size` custom
 * property set by the consuming section, so responsive overrides in CSS work.
 * An inline style here would win over every media query.
 */
export default function Icon3D({ name, fallbackSrc, alt = "", bare = false, className = "" }) {
  const holderRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Device class is checked first so phones never even build a WebGL context.
    setEnabled(canAfford3D() && !prefersReducedMotion() && hasWebGL());
  }, []);

  useEffect(() => {
    const el = holderRef.current;
    if (!el || !enabled) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "220px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);

  const show3D = enabled && inView;

  return (
    <span
      ref={holderRef}
      className={`icon3d ${bare ? "icon3d--bare" : ""} ${className} ${show3D ? "is-3d" : ""}`}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <span className="icon3d__glow" aria-hidden="true" />

      <img
        className="icon3d__flat"
        src={fallbackSrc}
        alt={alt}
        width="80"
        height="80"
        loading="lazy"
        decoding="async"
      />

      {show3D && (
        <span className="icon3d__stage" aria-hidden="true">
          <Suspense fallback={null}>
            <Icon3DCanvas name={name} hovered={hovered} />
          </Suspense>
        </span>
      )}
    </span>
  );
}
